/**
 * Client-side role matching logic, mirroring the backend RoleMatchingService.
 *
 * A freelancer is eligible to apply to a job when at least one of their featured
 * roles equals the job role, or equals a role that is listed as related to the
 * job role in the platform's role taxonomy.
 *
 * The algorithm intentionally matches the server-side implementation so that the
 * frontend can block ineligible submissions before they ever reach the API.
 */

/**
 * Parse the related_roles value returned by the API.
 *
 * The transformer sets a default of `[]`, but older rows may return a JSON
 * string (e.g. `'["Logo Designer","Poster Designer"]'`). Handle both.
 *
 * @param {string|Array} relatedRoles
 * @returns {string[]}
 */
export function parseRelatedRoles(relatedRoles) {
  if (!relatedRoles) return [];
  if (Array.isArray(relatedRoles)) return relatedRoles;
  if (typeof relatedRoles === 'string') {
    try {
      const parsed = JSON.parse(relatedRoles);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }
  return [];
}

/**
 * Find a suggested role object by name (case-insensitive).
 *
 * @param {string} roleName
 * @param {Array}  suggestedRoles  — the full list from the /suggestedRoles endpoint
 * @returns {Object|null}
 */
function findRoleByName(roleName, suggestedRoles) {
  if (!roleName || !Array.isArray(suggestedRoles)) return null;
  const needle = roleName.trim().toLowerCase();
  return suggestedRoles.find((r) => r.name && r.name.trim().toLowerCase() === needle) || null;
}

/**
 * Determine whether the freelancer's featured roles satisfy the job role.
 *
 * @param {string[]} featuredRoles  — user.featured_roles from the Redux store
 * @param {string}   jobRole        — brief.role
 * @param {Array}    suggestedRoles — full list from /suggestedRoles (includes related_roles)
 * @returns {boolean}
 */
export function featuredRolesMatchJobRole(featuredRoles, jobRole, suggestedRoles) {
  // Skill-only job: no role restriction
  if (!jobRole) return true;

  if (!Array.isArray(featuredRoles) || featuredRoles.length === 0) return false;

  // Build the set of acceptable roles: the job role itself + its related roles
  const roleEntry = findRoleByName(jobRole, suggestedRoles);
  const relatedRoles = roleEntry ? parseRelatedRoles(roleEntry.related_roles) : [];
  const acceptable = [jobRole, ...relatedRoles].map((r) => r.trim().toLowerCase());

  return featuredRoles.some((r) => acceptable.includes(r.trim().toLowerCase()));
}
